import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox
} from '@mui/material';
import { MIconButton } from 'components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { axios } from 'utils/axios';
import roundManageSearch from '@iconify/icons-ic/round-manage-search';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { DatePicker } from '@mui/lab';
import { CheckBox } from '@mui/icons-material';

interface FiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  onApply: () => void;
  onClear: () => void;
}

const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  open,
  onClose,
  filters,
  setFilters,
  onApply,
  onClear
}) => {
  const [councils, setCouncils] = useState<any>([]);

  const fetchAllCouncils = async () => {
    const response = await axios.get('/rest/api/council/area');
    const allCouncils = response.data.data.data || [];
    setCouncils(allCouncils?.map((el: any) => ({ value: el.CouncilArea, id: el.VEHICLE_TYPE_ID })));
  };

  const handleMultiCheckboxChange =
    (field: string, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      setFilters((prevFilters: any) => {
        const currentValues = prevFilters[field]?.split(',').filter((v: any) => v.trim()) || [];
        const updatedValues = isChecked
          ? Array.from(new Set([...currentValues, value])) // Add the value if checked
          : currentValues.filter((v: any) => v !== value); // Remove the value if unchecked

        return {
          ...prevFilters,
          [field]: updatedValues.join(',')
        };
      });
    };

  useEffect(() => {
    fetchAllCouncils();
  }, []);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <Typography variant="subtitle1" sx={{ ml: 1 }}>
          Filters
        </Typography>
        <MIconButton onClick={onClose}>
          <Icon icon={closeFill} width={20} height={20} />
        </MIconButton>
      </Stack>

      <Divider />
      <Stack spacing={3} sx={{ p: 3 }}>
        {/* Page Size */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Page Size
          </Typography>
          <FormControl fullWidth>
            <Select
              value={filters.pageSize}
              onChange={(e) => setFilters({ ...filters, pageSize: e.target.value })}
            >
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
            </Select>
          </FormControl>
        </div>
        {/* Search */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Search
          </Typography>
          <TextField
            label="Search"
            fullWidth
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        {/* Start Date */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Date From
          </Typography>

          <DatePicker
            label="Date From"
            inputFormat="dd/MM/yyyy"
            value={filters.dateFrom || null}
            onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
        {/* End Date */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Date To
          </Typography>
          <DatePicker
            label="Date To"
            inputFormat="dd/MM/yyyy"
            value={filters.dateTo || null}
            onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
        {/* Post Code */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Post Code
          </Typography>
          <TextField
            label="Post Code"
            fullWidth
            value={filters.postcode}
            onChange={(e) => setFilters({ ...filters, postcode: e.target.value })}
          />
        </div>
        {/* Council Area */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Council Area
          </Typography>
          <FormControl sx={{ width: '250px' }}>
            <InputLabel>Council Area</InputLabel>
            <Select
              value={filters.councilArea}
              onChange={(e) => setFilters({ ...filters, councilArea: e.target.value })}
              label="Council Area"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300, // Optional: Limits the height of the dropdown
                    width: '200px'
                  }
                }
              }}
            >
              {councils.map((option: any) => (
                <MenuItem
                  key={option.id}
                  value={option.value}
                  style={{
                    whiteSpace: 'normal', // Allow text to wrap
                    wordBreak: 'break-word' // Break words if they are too long
                  }}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {/* Address */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Address
          </Typography>
          <TextField
            label="Address"
            fullWidth
            value={filters.address}
            onChange={(e) => setFilters({ ...filters, address: e.target.value })}
          />
        </div>
        {/* Contact Name */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Contact Name
          </Typography>
          <TextField
            label="Contact Name"
            fullWidth
            value={filters.contactName}
            onChange={(e) => setFilters({ ...filters, contactName: e.target.value })}
          />
        </div>
        {/* Name */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Name
          </Typography>
          <TextField
            label="Name"
            fullWidth
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </div>
        {/* Owner (Checkboxes) */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Owner
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.isOwner || '').includes('1')}
                  onChange={handleMultiCheckboxChange('isOwner', '1')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.isOwner || '').includes('0')}
                  onChange={handleMultiCheckboxChange('isOwner', '0')}
                />
              }
              label="No"
            />
          </FormControl>
        </div>

        <div>
          <Typography variant="subtitle1" gutterBottom>
            Client
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.isClient || '').includes('1')}
                  onChange={handleMultiCheckboxChange('isClient', '1')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.isClient || '').includes('0')}
                  onChange={handleMultiCheckboxChange('isClient', '0')}
                />
              }
              label="No"
            />
          </FormControl>
        </div>
      </Stack>
      {/* Buttons */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="primary"
          variant="outlined"
          onClick={onApply}
          startIcon={<Icon icon={roundManageSearch} />}
        >
          Apply Filters
        </Button>
      </Box>
      <Box sx={{ p: 3 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="secondary"
          variant="outlined"
          onClick={onClear}
          startIcon={<Icon icon={roundFilterList} />}
        >
          Clear
        </Button>
      </Box>
    </Drawer>
  );
};

export default FiltersDrawer;
